// Right-side drawer that renders the step form(s) for the selected
// "What changed?" category, reusing the shared RightPanel (MUI right Drawer).
//
// The drawer edits a LOCAL DRAFT copy of the form values. Nothing is written
// back to the real Formik state (and therefore nothing shows up in the
// "Changes Requested" summary) until the user clicks "Save". Closing the
// drawer any other way (X / backdrop) discards the draft.

import React from "react";
import { Box, Button, Stack } from "@mui/material";
import produce from "immer";
import { RightPanel } from "../../../../../../../../components/RightPanel/RightPanel";
import { Dialog } from "../../../../../../../../components/Dialogs/Dialog";
import { IHabitationalPolicyChangeProps } from "../../Steps/HabitationalPolicyChange.types";
import { IHabitationalPolicyChange } from "../../../../../../../../redux/habitationalPolicy/habitationalPolicyChangeRequest/habitationalPolicyChangeReq.types";
import { NameChangeInformation } from "../../Steps/NameChange";
import { AddressChangeInformationV2 } from "./AddressChangeInformationV2";
import { DeletePickerV2 } from "./DeletePickerV2";
import { CreditCardInformationV2 } from "./CreditCardInformationV2";
import { CancellationReinstatementV2 } from "./CancellationReinstatementV2";
import { IChangeSelection } from "./WhatChangedBoard";
import { isRecordSection } from "./RecordPanelHost";

interface IChangeSectionDrawerProps extends IHabitationalPolicyChangeProps {
  selection: IChangeSelection | null;
  onClose: () => void;
}

// Sets a possibly-dotted path (e.g. "credit_card_info.expiry") on an immer
// draft, creating intermediate objects as needed.
const setPath = (obj: any, path: string, value: any) => {
  const keys = path.split(".");
  let cursor = obj;
  for (let i = 0; i < keys.length - 1; i += 1) {
    const key = keys[i];
    if (cursor[key] === null || cursor[key] === undefined) {
      cursor[key] = {};
    }
    cursor = cursor[key];
  }
  cursor[keys[keys.length - 1]] = value;
};

export const ChangeSectionDrawer: React.FC<IChangeSectionDrawerProps> = ({
  selection,
  onClose,
  ...stepProps
}) => {
  // Local draft of the form values, seeded from the real values each time a
  // new selection opens the drawer.
  const [draftValues, setDraftValues] =
    React.useState<IHabitationalPolicyChange>(stepProps.values);

  React.useEffect(() => {
    if (selection) {
      setDraftValues(stepProps.values);
    }
     
  }, [selection]);

  if (!selection) {
    return null;
  }

  // Draft-backed Formik-like handlers passed down to the section components so
  // their edits stay local until "Save".
  const draftSetValues = (
    update: React.SetStateAction<IHabitationalPolicyChange>
  ) => {
    setDraftValues((prev) =>
      typeof update === "function"
        ? (update as (p: IHabitationalPolicyChange) => IHabitationalPolicyChange)(
            prev
          )
        : update
    );
  };

  const draftSetFieldValue = (field: string, value: any) => {
    setDraftValues((prev) =>
      produce(prev, (d: any) => {
        setPath(d, field, value);
      })
    );
  };

  const draftHandleChange = (e: React.ChangeEvent<any>) => {
    const target = e?.target;
    if (!target) {
      return;
    }
    const field = target.name || target.id;
    if (!field) {
      return;
    }
    const value = target.type === "checkbox" ? target.checked : target.value;
    draftSetFieldValue(field, value);
  };

  const draftProps: IHabitationalPolicyChangeProps = {
    ...stepProps,
    values: draftValues,
    setValues: draftSetValues as IHabitationalPolicyChangeProps["setValues"],
    setFieldValue:
      draftSetFieldValue as IHabitationalPolicyChangeProps["setFieldValue"],
    handleChange:
      draftHandleChange as IHabitationalPolicyChangeProps["handleChange"],
  };

  const handleSave = () => {
    // Commit the draft to the real Formik state, then close.
    stepProps.setValues(draftValues);
    onClose();
  };

  const renderSection = () => {
    switch (selection.section) {
      case "nameChange":
        return <NameChangeInformation {...draftProps} />;
      case "addressChange":
        return <AddressChangeInformationV2 {...draftProps} />;
      case "paymentMethodChange":
        // Payment method is a single-object section (credit card / bank
        // details). Its fields are edited in the drawer draft and surface in
        // "Changes Requested" only after "Save".
        return <CreditCardInformationV2 {...draftProps} hideHeading />;
      case "cancellationReinstatement":
        // Cancellation / Reinstatement: Cancel Reason + Subtype. Single-object
        // section that writes cancellation_reason + sub_type on Save.
        return (
          <CancellationReinstatementV2 {...draftProps} mode={selection.mode} />
        );
      case "riskLocationChange":
      case "coverageChange":
      case "scheduleChange":
      case "interestedPartyChange":
        // Record sections only reach the drawer for "Delete" (Add opens the
        // record panel directly). Show just the delete picker.
        if (isRecordSection(selection.section)) {
          return <DeletePickerV2 {...draftProps} section={selection.section} />;
        }
        return null;
      case "multipleChange":
        return (
          <>
            <NameChangeInformation {...draftProps} />
            <AddressChangeInformationV2 {...draftProps} />
          </>
        );
      default:
        return null;
    }
  };

  // The Payment Method section lays its fields out in its own responsive rows
  // (per the mockup / matching Automobile V2), so it opts out of the full-row
  // flattening below and uses a wider panel.
  const isPaymentSection = selection.section === "paymentMethodChange";

  // Sections whose own Grid breakpoints are intended to be kept as-is (not
  // flattened) and that want the wider panel.
  const isWideSection = isPaymentSection;

  // Field content shared by both the drawer and the dialog presentation.
  const sectionContent = (
    <Box
      sx={{
        // Hide the shared step's own blue LabelCard heading bar — the
        // drawer/dialog header already shows the section title. The LabelCard
        // renders as the first (Box) child of each shared step, while the field
        // grid is a .MuiGrid-root, so this only hits the bar.
        "& > .MuiBox-root:first-of-type": { display: "none" },
        // Force the field grid to wrap into 2-3 rows instead of one wide row.
        // Wide sections (e.g. Payment Method) keep their own breakpoints.
        ...(!isWideSection && {
          "& .MuiGrid-container > .MuiGrid-item": {
            flexBasis: { xs: "100%", sm: "50%" },
            maxWidth: { xs: "100%", sm: "50%" },
          },
        }),
      }}
    >
      {renderSection()}
    </Box>
  );

  // Multiple Change is presented as a centered dialog; every other section
  // uses the right-side panel.
  if (selection.section === "multipleChange") {
    return (
      <Dialog
        open={true}
        size="lg"
        fullWidth
        title={selection.heading}
        subtitle="Update the fields for this change, then click Save to add it to the request."
        onClose={onClose}
        actions={[
          {
            type: "button",
            variant: "contained",
            label: "Save",
            onClick: handleSave,
          },
        ]}
      >
        {sectionContent}
      </Dialog>
    );
  }

  return (
    <RightPanel
      open={true}
      version="v2"
      width={isWideSection ? "70%" : "42%"}
      heading={selection.heading}
      subHeading="Update the fields for this change, then click Save to add it to the request."
      onClose={onClose}
      drawerProps={{ zIndex: 1400 }}
      actionButtons={() => (
        <Stack
          direction="row"
          justifyContent="flex-start"
          spacing={2}
          sx={{
            position: "relative",
            zIndex: (theme) => theme.zIndex.modal + 2,
            pb: 1,
          }}
        >
          <Button variant="contained" onClick={handleSave}>
            Save
          </Button>
        </Stack>
      )}
    >
      {sectionContent}
    </RightPanel>
  );
};
